import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button, Input, InputGroup, InputGroupText } from 'reactstrap';
import '../css/ProductList.css';
import { Search, ArrowRight, ArrowLeft, ChevronLeft, ChevronRight, Grid3x3GapFill, ListUl } from 'react-bootstrap-icons';
import { Link, useLocation } from 'react-router-dom';
import ProdView from '../components/ProdView';
import fallbackImage from '../assets/fallback.jpg';
import SeoHead from './Shared/SeoHead';

const sortProductsNewestFirst = (items) => {
    const list = Array.isArray(items) ? [...items] : [];

    return list.sort((a, b) => {
        const aName = (a?.proD_NAME || '').trim();
        const bName = (b?.proD_NAME || '').trim();
        const aLower = aName.toLowerCase();
        const bLower = bName.toLowerCase();

        const aIsNx64 = aLower.includes('nx64');
        const bIsNx64 = bLower.includes('nx64');
        if (aIsNx64 !== bIsNx64) {
            return aIsNx64 ? -1 : 1;
        }

        const aHasNx = aLower.includes('nx');
        const bHasNx = bLower.includes('nx');
        if (aHasNx !== bHasNx) {
            return aHasNx ? -1 : 1;
        }

        return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
    });
};

const Products = () => {
    const location = useLocation();
    const state = location?.state || {};
    const shouldRestoreState = state?.restoreFilters === true && Array.isArray(state.products) && state.products.length > 0;

    // selCat / searchTerm should always come from nav state, regardless of restoreFilters
    const {
        cat: stateCat = [],
        products: stateProducts = [],
        selCat = 0,
        searchTerm: initialSearchTerm = ''
    } = state;

    const [loading, setLoading] = useState(true);
    const [cat, setCat] = useState(Array.isArray(stateCat) ? stateCat : []);
    const [products, setProducts] = useState(Array.isArray(stateProducts) ? stateProducts : []);
    const [content, setContent] = useState([]);
    const [filterBy, setFilterBy] = useState(selCat || 0);
    const [showProductList, setShowProductList] = useState(true);
    const [selectedProdID, setSelectedProdID] = useState(null);
    const [imageDirectory, setImageDirectory] = useState('');
    const [documents, setDocuments] = useState(null);
    const [searchText, setSearchText] = useState(initialSearchTerm || '');
    const [viewMode, setViewMode] = useState('grid'); // 'list' | 'grid'

    const bearerToken = localStorage.getItem('bearerToken');

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = viewMode === 'list' ? 10 : 15;

    // Fast lookup: cat_id -> cat_name
    const categoryMap = useMemo(() => {
        const map = {};
        if (Array.isArray(cat)) {
            cat.forEach(c => { map[String(c.caT_ID)] = c.caT_NAME; });
        }
        return map;
    }, [cat]);

    useEffect(() => {
        if (shouldRestoreState && Array.isArray(stateProducts) && stateProducts.length > 0) {
            const restoredProducts = sortProductsNewestFirst(stateProducts);
            setLoading(true);
            setProducts(restoredProducts);
            setContent(restoredProducts);
            getDirectories();
            applyFilters(selCat, initialSearchTerm, restoredProducts);
        } else {
            setLoading(true);
            const fetchAll = async () => {
                try {
                    const [dirsRes, catsRes, prodsRes] = await Promise.all([
                        axios.get('/Website/GetDirectories', { headers: { Authorization: `Bearer ${bearerToken}` } }),
                        axios.get('/Website/GetCategories', { headers: { Authorization: `Bearer ${bearerToken}` } }),
                        axios.get('/Website/getProducts', { headers: { Authorization: `Bearer ${bearerToken}` } }),
                    ]);
                    setImageDirectory(dirsRes.data?.imgDirectory || '');
                    setDocuments(dirsRes.data?.docsDirectory || '');
                    const fetchedCats = Array.isArray(catsRes.data) ? catsRes.data : [];
                    const fetchedProds = sortProductsNewestFirst(prodsRes.data);
                    setCat(fetchedCats);
                    setProducts(fetchedProds);
                    applyFilters(selCat, initialSearchTerm, fetchedProds); // <-- apply the incoming category filter
                } catch (error) {
                    console.error('Error loading products:', error);
                } finally {
                    setLoading(false);
                }
            };
            fetchAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSearchText(initialSearchTerm || '');
        setCurrentPage(1);
        applyFilters(selCat, initialSearchTerm, products.length > 0 ? products : undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSearchTerm, selCat]);

    const getDirectories = async () => {
        try {
            const response = await axios.get('/Website/GetDirectories', {
                headers: { Authorization: `Bearer ${bearerToken}` }
            });
            setImageDirectory(response.data?.imgDirectory || '');
            setDocuments(response.data?.docsDirectory || '');
        } catch (error) {
            console.error('Error fetching image directory:', error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = (catID, term, sourceProducts) => {
        const id = parseInt(catID, 10) || 0;
        setFilterBy(id);

        let list = Array.isArray(sourceProducts) ? sortProductsNewestFirst(sourceProducts) : Array.isArray(products) ? sortProductsNewestFirst(products) : [];

        if (id !== 0) {
            list = list.filter(item => String(item.caT_ID) === String(id));
        }

        const t = (term || '').trim().toLowerCase();
        if (t) {
            list = list.filter(item =>
                item.proD_NAME?.toLowerCase().includes(t) ||
                item.proD_DESC?.toLowerCase().includes(t)
            );
        }

        setContent(list);
    };

    const handleCategoryChange = (e) => {
        const id = parseInt(e.target.value, 10) || 0;
        setCurrentPage(1);
        applyFilters(id, searchText);
    };

    const searchProducts = (text) => {
        setSearchText(text);
        setCurrentPage(1);
        applyFilters(filterBy, text);
    };

    const totalPages = Math.max(1, Math.ceil(content.length / itemsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const paginatedProducts = content.slice(
        (safePage - 1) * itemsPerPage,
        safePage * itemsPerPage
    );

    const changePage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Compact pagination (max 7 buttons with ellipses).
    const buildPageList = () => {
        const pages = [];
        const max = totalPages;
        if (max <= 7) {
            for (let i = 1; i <= max; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        if (safePage > 4) pages.push('…');
        const start = Math.max(2, safePage - 1);
        const end = Math.min(max - 1, safePage + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (safePage < max - 3) pages.push('…');
        pages.push(max);
        return pages;
    };

    const truncate = (text, len = 180) => {
        if (!text) return '';
        const clean = String(text).replace(/\s+/g, ' ').trim();
        return clean.length > len ? clean.slice(0, len).trimEnd() + '…' : clean;
    };

    const getImageSrc = (product) => product.imG_PATH && imageDirectory ? `${imageDirectory}/${product.imG_PATH}` : fallbackImage;

    const activeCategoryName = filterBy !== 0 ? categoryMap[String(filterBy)] : '';
    const hasActiveFilters = Boolean(searchText || filterBy !== 0);
    const siteUrl = window.location.origin;
    const productsTitle = activeCategoryName ? `MHE Electronics ${activeCategoryName} | Official Product Catalog` : 'MHE Electronics Product Catalog | Official Manufacturer';
    const productsDescription = activeCategoryName ? `Browse ${activeCategoryName} from MHE Electronics, the original manufacturer of MCS and MCS-NX control systems and related electronic products.` : 'Browse the official MHE Electronics product catalog. MHE Electronics is the original manufacturer of MCS and MCS-NX control systems and related electronic products.';
    const productsSchema = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: productsTitle,
        description: productsDescription,
        url: `${siteUrl}/products`,
        isPartOf: {
            '@type': 'WebSite',
            name: 'MHE Electronics',
            url: siteUrl
        }
    };

    if (loading) {
        return (
            <div className="products-loading-screen">
                <div className="products-loader-ring">
                    <svg className="products-loader-svg" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="40" cy="40" r="34" stroke="#e5e7eb" strokeWidth="8" />
                        <circle cx="40" cy="40" r="34" stroke="#ac130a" strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray="100 114"
                            strokeDashoffset="25" />
                    </svg>
                </div>
                <p className="products-loading-label">Loading products…</p>
            </div>
        );
    }

    return (
        <div className="products-page">
            <SeoHead title={productsTitle} description={productsDescription} canonical={`${siteUrl}/products`} schema={productsSchema}/>
            {showProductList ? (
                <>
                    {/* ===== Header ===== */}
                    <div className="products-hero">
                        <div className="products-hero-main">
                            <div className="products-header">
                                <div className="products-header-text">
                                    <h1 className="products-title">MHE Electronics Product Catalog</h1>
                                    <p className="products-subtitle">
                                        Emergency warning systems, control modules and accessories — engineered for professionals.
                                    </p>
                                </div>
                            </div>

                            <div className="products-summary-row">
                                <div className="products-stat-card">
                                    <span className="products-stat-value">{content.length}</span>
                                    <span className="products-stat-label">Results</span>
                                </div>
                                <div className="products-stat-card products-stat-card-wide">
                                    <span className="products-stat-label">Current view</span>
                                    <span className="products-stat-detail">
                                        {activeCategoryName || 'All Products'}
                                        {searchText ? ` • “${searchText}”` : ' • No keyword filter'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="products-controls-card">
                            <div className="products-controls-top">
                                <div>
                                    <span className="products-controls-kicker">Quick tools</span>
                                    <h2 className="products-controls-title">Search and filter</h2>
                                </div>

                                <div className="products-view-toggle" role="group" aria-label="View mode">
                                    <button
                                        type="button"
                                        className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                                        onClick={() => { setViewMode('list'); setCurrentPage(1); }}
                                        aria-label="List view"
                                        title="List view"
                                    >
                                        <ListUl size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                        onClick={() => { setViewMode('grid'); setCurrentPage(1); }}
                                        aria-label="Grid view"
                                        title="Grid view"
                                    >
                                        <Grid3x3GapFill size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="products-toolbar">
                                <div className="products-toolbar-search">
                                    <label className="products-field-label" htmlFor="products-search">
                                        Search products
                                    </label>
                                    <InputGroup className="products-search-group">
                                        <InputGroupText className="products-search-icon">
                                            <Search size={18} />
                                        </InputGroupText>
                                        <Input
                                            id="products-search"
                                            placeholder="Search by name or description"
                                            type="text"
                                            autoComplete="search"
                                            value={searchText}
                                            onChange={(e) => searchProducts(e.target.value)}
                                            className="products-search-input"
                                        />
                                    </InputGroup>
                                </div>

                                <div className="products-toolbar-filter">
                                    <label className="products-field-label" htmlFor="products-category-filter">
                                        Category
                                    </label>
                                    <select id="products-category-filter"
                                        className="form-select form-select-lg products-filter-select"
                                        value={filterBy}
                                        onChange={handleCategoryChange}
                                        aria-label="Filter by category"
                                    >
                                        <option value="0">All Products</option>
                                        {Array.isArray(cat) && cat.map(c => (
                                            <option key={c.caT_ID} value={c.caT_ID}>
                                                {c.caT_NAME}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="products-toolbar-footer">
                                <p className="products-filter-summary">
                                    Showing {content.length} {content.length === 1 ? 'product' : 'products'}
                                    {activeCategoryName ? ` in ${activeCategoryName}` : ''}
                                    {searchText ? ` matching “${searchText}”` : ''}
                                </p>
                                {hasActiveFilters && (
                                    <Button className="products-clear-btn"
                                        onClick={() => {
                                            setSearchText('');
                                            setCurrentPage(1);
                                            applyFilters(0, '');
                                        }}
                                    >
                                        Clear filters
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ===== Empty state ===== */}
                    {content.length === 0 ? (
                        <div className="products-empty">
                            <h4>No products found</h4>
                            <p>Try clearing your search or selecting a different category.</p>
                            <Button
                                className="products-empty-btn"
                                onClick={() => {
                                    setSearchText('');
                                    setCurrentPage(1);
                                    applyFilters(0, '');
                                }}
                            >
                                Reset filters
                            </Button>
                        </div>
                    ) : (
                        <>
                            {/* ===== List view ===== */}
                            {viewMode === 'list' ? (
                                <ul className="product-list">
                                    {paginatedProducts.map((product, idx) => {
                                        const catName = categoryMap[String(product.caT_ID)];
                                        return (
                                            <li key={product.proD_ID || idx} className="product-list-item">
                                                <Link
                                                    to={`/ProdView/${product.proD_NAME}`}
                                                    state={{
                                                        cat,
                                                        products,
                                                        selCat: filterBy,
                                                        searchTerm: searchText,
                                                        currentPage: safePage,
                                                        viewMode,
                                                        imgDirectory: imageDirectory,
                                                        docsDirectory: documents,
                                                        prod: product
                                                    }}
                                                    className="product-list-link"
                                                >
                                                    <div className="product-list-image">
                                                        <img
                                                            src={getImageSrc(product)}
                                                            alt={product.proD_NAME || 'Product'}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = fallbackImage;
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="product-list-body">
                                                        {catName && (
                                                            <span className="product-badge">{catName}</span>
                                                        )}
                                                        <h3 className="product-list-name">
                                                            {product.proD_NAME}
                                                        </h3>
                                                        <p className="product-list-desc">
                                                            {truncate(product.proD_DESC) || 'No description available.'}
                                                        </p>
                                                    </div>
                                                    <div className="product-list-cta">
                                                        <span className="product-cta-label">View details</span>
                                                        <ArrowRight size={18} />
                                                    </div>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                /* ===== Grid view ===== */
                                <div className="product-grid">
                                    {paginatedProducts.map((product, idx) => {
                                        const catName = categoryMap[String(product.caT_ID)];
                                        return (
                                            <Link
                                                key={product.proD_ID || idx}
                                                to={`/ProdView/${product.proD_NAME}`}
                                                state={{
                                                    cat,
                                                    products,
                                                    selCat: filterBy,
                                                    searchTerm: searchText,
                                                    currentPage: safePage,
                                                    viewMode,
                                                    imgDirectory: imageDirectory,
                                                    docsDirectory: documents,
                                                    prod: product
                                                }}
                                                className="product-card"
                                            >
                                                <div className="product-card-image">
                                                    <img
                                                        src={getImageSrc(product)}
                                                        alt={product.proD_NAME || 'Product'}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = fallbackImage;
                                                        }}
                                                    />
                                                </div>
                                                <div className="product-card-body">
                                                    {catName && (
                                                        <span className="product-badge">{catName}</span>
                                                    )}
                                                    <h5 className="product-card-name">{product.proD_NAME}</h5>
                                                    <p className="product-card-desc">
                                                        {truncate(product.proD_DESC, 100)}
                                                    </p>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}

                            {/* ===== Pagination ===== */}
                            {totalPages > 1 && (
                                <nav className="products-pagination" aria-label="Pagination">
                                    <button type="button" className="page-btn" onClick={() => changePage(safePage - 1)} disabled={safePage === 1} aria-label="Previous page">
                                        <ChevronLeft size={16} />
                                    </button>
                                    {buildPageList().map((p, i) =>
                                        p === '…' ? (
                                            <span key={`e-${i}`} className="page-ellipsis">…</span>
                                        ) : (
                                            <button
                                                key={p}
                                                type="button"
                                                className={`page-btn ${safePage === p ? 'active' : ''}`}
                                                onClick={() => changePage(p)}
                                                aria-current={safePage === p ? 'page' : undefined}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}
                                    <button type="button" className="page-btn" onClick={() => changePage(safePage + 1)} disabled={safePage === totalPages} aria-label="Next page">
                                        <ChevronRight size={16} />
                                    </button>
                                </nav>
                            )}
                        </>
                    )}
                </>
            ) : (
                <div>
                    <Button color="link" className="back-button"
                        onClick={() => {
                            setSelectedProdID(null);
                            setShowProductList(true);
                        }}
                    >
                        <ArrowLeft className="back-icon" size={20} color="black" />
                        <span className="back-text">Back to Products</span>
                    </Button>
                    <ProdView prod={selectedProdID} />
                </div>
            )}
        </div>
    );
};

export default Products;